import React, { useState, useRef } from 'react';
import { Download, Loader, FileSpreadsheet, CheckCircle } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import axios from 'axios';
import './Invoices.css';

export default function Invoices() {
  const invoiceRef = useRef();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // --- STATE ---
  const [dateRange, setDateRange] = useState({
    from: new Date().toISOString().slice(0, 10),
    to: new Date().toISOString().slice(0, 10)
  });

  // Invoice Number Logic: TAP + mm + yyyy
  const generateInvoiceNo = () => {
    const today = new Date();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    return `TAP${mm}${yyyy}`;
  };

  const [invoiceNo] = useState(generateInvoiceNo());

  // Panel Details
  const [panelDetails, setPanelDetails] = useState({
    name: '',
    address: '',
    pan: '',
    panelId: '',
    bankName: '',
    accHolderName: '',
    accNumber: '',
    ifsc: ''
  });

  // Signature Names (The names you type above the line)
  const [signatureNames, setSignatureNames] = useState({
    panel: '',
    verifier: ''
  });

  // Items
  const [items, setItems] = useState([]);

  // --- HANDLERS ---
  const handleDateChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  const handlePanelChange = (e) => {
    setPanelDetails({ ...panelDetails, [e.target.name]: e.target.value });
  };

  const handleSignatureChange = (e) => {
    setSignatureNames({ ...signatureNames, [e.target.name]: e.target.value });
  };

  // --- AI FILE UPLOAD ---
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/invoices/extract`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { panelDetails: aiPanel, items: aiItems } = response.data;

      setPanelDetails(prev => ({
        ...prev,
        name: aiPanel.name || '',
        address: aiPanel.address || '',
        pan: aiPanel.pan || '',
        panelId: aiPanel.panelId || '',
        bankName: aiPanel.bankName || '',
        accHolderName: aiPanel.accHolderName || aiPanel.name || '',
        accNumber: aiPanel.accNumber || '',
        ifsc: aiPanel.ifsc || ''
      }));

      if (aiItems && aiItems.length > 0) {
        setItems(aiItems.map(item => ({ ...item, id: Math.random() })));
        setSuccess(true);
      } else {
        alert("AI processed the file but found no billable items.");
      }

    } catch (error) {
      console.error("AI Error:", error);
      alert("AI Extraction Failed.");
    } finally {
      setLoading(false);
      e.target.value = null;
    }
  };

  // --- CALCULATIONS ---
  const totalAmount = items.reduce((sum, item) => sum + Number(item.amount), 0);

  const numberToWords = (num) => {
    if (num === 0) return "Zero";
    const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const inWords = (n) => {
        if ((n = n.toString()).length > 9) return 'overflow';
        let n_array = ('000000000' + n).slice(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
        if (!n_array) return; 
        let str = '';
        str += (Number(n_array[1]) !== 0) ? (a[Number(n_array[1])] || b[n_array[1][0]] + ' ' + a[n_array[1][1]]) + 'Crore ' : '';
        str += (Number(n_array[2]) !== 0) ? (a[Number(n_array[2])] || b[n_array[2][0]] + ' ' + a[n_array[2][1]]) + 'Lakh ' : '';
        str += (Number(n_array[3]) !== 0) ? (a[Number(n_array[3])] || b[n_array[3][0]] + ' ' + a[n_array[3][1]]) + 'Thousand ' : '';
        str += (Number(n_array[4]) !== 0) ? (a[Number(n_array[4])] || b[n_array[4][0]] + ' ' + a[n_array[4][1]]) + 'Hundred ' : '';
        str += (Number(n_array[5]) !== 0) ? ((str !== '') ? 'and ' : '') + (a[Number(n_array[5])] || b[n_array[5][0]] + ' ' + a[n_array[5][1]]) : '';
        return str;
    };
    return inWords(Math.floor(num)) + " Rupees Only";
  };

  // --- PDF DOWNLOAD ---
  const downloadPDF = async () => {
    const element = invoiceRef.current;
    
    const canvas = await html2canvas(element, { 
        scale: 3, 
        useCORS: true,
        backgroundColor: '#ffffff'
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`Invoice_${invoiceNo}.pdf`);
  };

  return (
    <div className="invoice-container">
      
      {/* SIDEBAR */}
      <div className="invoice-controls">
        {/* Scrollable Content */}
        <div className="controls-scroll-area">
            <h3 className="control-title">Invoice Settings</h3>
            
            <div className="control-group">
                <label>Billing Period</label>
                <div className="date-row">
                    <input type="date" value={dateRange.from} onChange={(e) => handleDateChange('from', e.target.value)} />
                    <span className="to-sep">to</span>
                    <input type="date" value={dateRange.to} onChange={(e) => handleDateChange('to', e.target.value)} />
                </div>
            </div>

            <div className={`upload-box ${loading ? 'loading' : ''} ${success ? 'success' : ''}`}>
                <input type="file" accept=".xlsx, .xls" onChange={handleFileUpload} disabled={loading} />
                <div className="upload-content">
                    {loading ? <Loader className="spin" size={32} /> : success ? <CheckCircle size={32} color="#10b981" /> : <FileSpreadsheet size={32} color="#6366f1" />}
                    <span className="upload-main-text">{loading ? "AI Processing..." : success ? "Data Extracted!" : "Upload Excel Data"}</span>
                    {!loading && !success && <span className="upload-sub-text">Drag & drop or click to browse</span>}
                </div>
                {!loading && !success && <div className="ai-badge">✨ AI Powered</div>}
            </div>

            <div className="manual-section">
                <label>Panel Details (Manual Edit)</label>
                <input name="name" placeholder="Panel Name" value={panelDetails.name} onChange={handlePanelChange} />
                <input name="panelId" placeholder="Panel ID" value={panelDetails.panelId} onChange={handlePanelChange} />
                <input name="pan" placeholder="PAN Number" value={panelDetails.pan} onChange={handlePanelChange} />
            </div>

            <div className="manual-section">
                <label>Signer Names (Appears above line)</label>
                <input name="panel" placeholder="Panel Name (e.g. John Doe)" value={signatureNames.panel} onChange={handleSignatureChange} />
                <input name="verifier" placeholder="Verifier Name" value={signatureNames.verifier} onChange={handleSignatureChange} />
            </div>
        </div>

        {/* Fixed Footer for Download Button */}
        <div className="controls-footer">
            <button className="download-btn" onClick={downloadPDF}>
            <Download size={18} /> Download Invoice PDF
            </button>
        </div>
      </div>

      {/* PREVIEW AREA */}
      <div className="invoice-preview-area">
        <div className="invoice-paper" ref={invoiceRef}>
            
            <div className="inv-header">
                <h1 className="brand-title">INVOICE</h1>
                <div className="inv-meta">
                    <div className="meta-row"><span>Invoice No:</span> <strong>{invoiceNo}</strong></div>
                    <div className="meta-row"><span>Date:</span> {new Date(dateRange.from).toLocaleDateString()} to {new Date(dateRange.to).toLocaleDateString()}</div>
                </div>
            </div>
            <div className="divider-line"></div>

            <div className="inv-addresses">
                <div className="addr-col">
                    <h4 className="addr-label">To,</h4>
                    <h3 className="addr-name">Teneo Advisoropedia</h3>
                    <p className="addr-text">Interview Service Partner</p>
                </div>
                <div className="addr-col right">
                    <h4 className="addr-label">From,</h4>
                    <h3 className="addr-name">{panelDetails.name || '[Panel Name]'}</h3>
                    <p className="addr-text">{panelDetails.address}</p>
                    <div className="addr-details">
                        <p><strong>PAN:</strong> {panelDetails.pan || '--'}</p>
                        <p><strong>ID:</strong> {panelDetails.panelId || '--'}</p>
                    </div>
                </div>
            </div>

            <div className="service-box">
                <strong>Service:</strong> Interview Service
            </div>

            <table className="inv-table">
                <thead>
                    <tr>
                        <th width="22%">Customer</th>
                        <th width="15%">Skill</th>
                        <th width="25%">Candidate Name</th>
                        <th width="18%">Date</th>
                        <th width="20%" className="text-right">Amount (INR)</th>
                    </tr>
                </thead>
                <tbody>
                    {items.length > 0 ? items.map((item, idx) => (
                        <tr key={idx}>
                            <td>{item.customer}</td>
                            <td>{item.skill}</td>
                            <td>{item.candidateName}</td>
                            <td>{item.interviewDate}</td>
                            <td className="text-right">{item.amount.toLocaleString('en-IN')}</td>
                        </tr>
                    )) : (
                        <tr><td colSpan="5" className="empty-row">No Data Loaded via AI</td></tr>
                    )}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan="4" className="text-right bold">Total</td>
                        <td className="text-right bold">{totalAmount.toLocaleString('en-IN')}</td>
                    </tr>
                </tfoot>
            </table>

            <p className="amount-words">
                <strong>Amount in Words:</strong> <i>{numberToWords(totalAmount)}</i>
            </p>

            <div className="bank-section">
                <h4>Bank Details:</h4>
                <div className="bank-grid">
                    <div><span>Bank Name:</span> <strong>{panelDetails.bankName || '--'}</strong></div>
                    <div><span>IFSC Code:</span> <strong>{panelDetails.ifsc || '--'}</strong></div>
                    <div className="full-width"><span>A/C Holder:</span> <strong>{panelDetails.accHolderName || '--'}</strong></div>
                    <div className="full-width"><span>Account No:</span> <strong>{panelDetails.accNumber || '--'}</strong></div>
                </div>
            </div>

            {/* UPDATED SIGNATURE SECTION */}
            <div className="signatures">
                <div className="sig-block">
                    {/* Name appears ABOVE the line */}
                    <div className="sig-name-display">{signatureNames.panel || ''}</div> 
                    <div className="sig-line"></div>
                    <p>Panel's Signature</p>
                </div>
                <div className="sig-block">
                    {/* Name appears ABOVE the line */}
                    <div className="sig-name-display">{signatureNames.verifier || ''}</div>
                    <div className="sig-line"></div>
                    <p>Verifier Name & Signature</p>
                    <span className="sig-date">Date: {new Date().toLocaleDateString()}</span>
                </div>
            </div>

        </div>
      </div>
    </div>
  );
}